import type { PlayEvent, PlayEventFormat, PlayRoundStatus } from '../types/playEvent';

export interface SocialRoundReallocationState {
  roundNumber: number;
  status: PlayRoundStatus;
  hasCompletedMatches: boolean;
}

export function canWithdrawFromTournamentEvent(
  event: Pick<PlayEvent, 'format' | 'startsAt' | 'withdrawDeadlineAt' | 'withdrawHoursBeforeStart'>,
  now: Date = new Date()
): boolean {
  if (event.format === 'social_rotate') return false;
  if (event.withdrawDeadlineAt) {
    return now.getTime() < event.withdrawDeadlineAt.getTime();
  }
  if (event.withdrawHoursBeforeStart != null) {
    const deadline = event.startsAt.getTime() - event.withdrawHoursBeforeStart * 60 * 60 * 1000;
    return now.getTime() < deadline;
  }
  return false;
}

export function canWithdrawFromNextSocialMatch(
  event: Pick<PlayEvent, 'format' | 'nextMatchWithdrawMinutes'>,
  nextMatchStart: Date | null | undefined,
  now: Date = new Date()
): boolean {
  if (event.format !== 'social_rotate') return false;
  if (!nextMatchStart) return true;
  const windowMs = event.nextMatchWithdrawMinutes * 60 * 1000;
  return now.getTime() < nextMatchStart.getTime() - windowMs;
}

export function canReallocateSocialRound(
  roundStatus: PlayRoundStatus,
  hasCompletedMatches: boolean
): boolean {
  if (roundStatus === 'completed') return false;
  if (hasCompletedMatches) return false;
  return true;
}

export function roundHasCompletedMatches(
  matches: Array<{ roundNumber: number; result: string }>,
  roundNumber: number
): boolean {
  return matches.some(
    (match) =>
      match.roundNumber === roundNumber &&
      match.result !== 'unplayed' &&
      match.result !== 'bye'
  );
}

/** First round number that may be re-paired; skips in-progress rounds on court. */
export function findFirstReallocationRound(
  rounds: SocialRoundReallocationState[],
  roundCount: number
): number | null {
  if (roundCount < 1) return null;
  const byNumber = new Map(rounds.map((round) => [round.roundNumber, round]));
  for (let roundNumber = 1; roundNumber <= roundCount; roundNumber += 1) {
    const round = byNumber.get(roundNumber);
    if (!round) {
      return roundNumber;
    }
    if (!canReallocateSocialRound(round.status, round.hasCompletedMatches)) {
      continue;
    }
    if (round.status === 'in_progress') {
      continue;
    }
    return roundNumber;
  }
  return null;
}
